import { AutocompleteRenderOptionState, Box } from '@mui/material';
import { ChipAutocomplete, PoolButton, PoolModal } from '@synergy/components';
import { FormattedMessage } from 'react-intl';
import { TopicListItem } from '@synergy/models';
import { getLocalizedString } from '@synergy/utils';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';

import { fetchAvailableTopicsForCustomer } from '../../../../../configuration/organization/topics/store/topics-actions';
import { localesSelector } from '../../../../../../common/selectors/locale-selectors';
import { useAppSelector } from '../../../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../../../store/use-thunk-dispatch';

export interface ChangeEmployeeTopicsModalProps {
    topics?: TopicListItem[];
    isVisible?: boolean;
    onConfirm: (tags: TopicListItem[]) => void;
    onCancel: () => void;
}

export const ChangeEmployeeTopicsModal = (props: ChangeEmployeeTopicsModalProps) => {
    const { topics: propTopics, isVisible = false, onConfirm, onCancel } = props;
    const { id: customerIdParam } = useParams<{ id: string }>();
    const dispatch = useThunkDispatch();
    const availableTopics = useAppSelector(state => state.topics.topics.items);
    const locales = useAppSelector(localesSelector);
    const [topics, setTopics] = useState<TopicListItem[]>(propTopics ?? []);

    useEffect(() => {
        setTopics(propTopics ?? []);
    }, [propTopics]);

    const handleTopicsOpen = () => {
        if (customerIdParam && !isNaN(Number(customerIdParam))) {
            dispatch(fetchAvailableTopicsForCustomer(Number(customerIdParam)));
        }
    };

    const handleConfirm = () => {
        onConfirm(topics);
        setTopics([]);
    };
    const handleCancel = () => {
        onCancel();
        setTopics([]);
    };

    const renderOption = (topic: TopicListItem, state: AutocompleteRenderOptionState) => {
        const title = getLocalizedString(locales, topic.title);
        const nameMatches = match(title, state.inputValue);
        const nameParts = parse(title, nameMatches);

        return (
            <Box>
                {nameParts.map(namePart => (
                    <Box component="span" sx={{ color: namePart.highlight ? 'primary.main' : undefined }}>
                        {namePart.text}
                    </Box>
                ))}
            </Box>
        );
    };

    return (
        <PoolModal
            title={<FormattedMessage id="app.contactsAndOrganizations.detail.changeTopics" />}
            isVisible={isVisible}
            actionChildren={[
                <PoolButton key="1" variant="text" styleType="firstLevel" onClick={handleCancel}>
                    <FormattedMessage id="app.common.cancel" />
                </PoolButton>,
                <PoolButton key="2" styleType="firstLevel" onClick={handleConfirm}>
                    <FormattedMessage id={'app.common.save'} />
                </PoolButton>,
            ]}
            handleClose={handleCancel}
        >
            <ChipAutocomplete
                selectAll={true}
                noOptionsText={<FormattedMessage id="app.common.emptyResult" />}
                options={availableTopics}
                renderOption={renderOption}
                getOptionLabel={(topic: TopicListItem) => getLocalizedString(locales, topic.title)}
                getChipLabel={(topic: TopicListItem) => getLocalizedString(locales, topic.title)}
                onChange={(_event, values) => setTopics(values)}
                value={topics}
                textfieldPlaceholder="app.select.placeholder.topics"
                onOpen={handleTopicsOpen}
                isOptionEqualToValue={(option: TopicListItem, value: TopicListItem) => option.id === value.id}
            />
        </PoolModal>
    );
};
